/**
 * Location type detection and radius determination utilities
 *
 * @module utils/locationType
 */

/**
 * Detects location type from Google Geocoding API response
 *
 * Only checks top-level types. If none match, returns null (defaults to 10 miles).
 *
 * @param {Object} geocodeResponse - Google Geocoding API response
 * @returns {string|null} - 'county', 'locality', 'postal_code', or null
 */
// Localities that should be treated as counties (1mi radius)
const COUNTY_OVERRIDES = ['london'];

export function detectLocationType(geocodeResponse) {
  if (!geocodeResponse) {
    return null;
  }

  // Only check top-level types - keep it simple
  if (geocodeResponse.types?.includes('locality')) {
    // London is a locality but should be treated as a county (1mi radius)
    const name = (geocodeResponse.formatted_address || '').toLowerCase();
    if (COUNTY_OVERRIDES.some((o) => name.startsWith(o))) {
      return 'county';
    }
    return 'locality';
  }
  if (geocodeResponse.types?.includes('postal_code')) {
    return 'postal_code';
  }
  // UK counties: colloquial_area (e.g. Cumbria, Bedfordshire) or administrative_area_level_2 (e.g. Kent, Surrey)
  if (geocodeResponse.types?.includes('colloquial_area')
    || geocodeResponse.types?.includes('administrative_area_level_2')
  ) {
    return 'county';
  }
  // Isle of Wight: Google returns as establishment + natural_feature
  if (geocodeResponse.types?.includes('natural_feature')) {
    return 'county';
  }

  // If none of the recognized types match, return null (defaults to 10 miles)
  return null;
}

/**
 * Determines radius based on location type
 *
 * Defaults: county=1, locality=10, postal_code=10, unknown=10
 * User override takes precedence
 *
 * @param {string|null} locationType - Location type from detectLocationType
 * @param {number|null} userSelectedRadius - User override (miles)
 * @returns {number} - Radius in miles
 */
export function determineRadius(locationType, userSelectedRadius = null) {
  if (userSelectedRadius != null) {
    return userSelectedRadius;
  }

  const defaults = {
    county: 1,
    locality: 10,
    postal_code: 10,
  };

  return defaults[locationType] ?? 10;
}
